import { Label, PolarAngleAxis, PolarRadiusAxis, RadialBar, RadialBarChart } from "recharts";
import { ChartErrorBoundary } from "./ChartErrorBoundary";
import { ChartContainer } from "./ui/chart";

function getDIndexDescription(dIndex: number): string {
  if (dIndex <= 0) return "No aurora activity";
  if (dIndex < 1.5) return "Shapeless Auroral Glow";
  if (dIndex < 2.5) return "Auroral arc";
  if (dIndex < 3.5) return "Auroral arc with beams";
  if (dIndex < 4.5) return "Auroral arc with pickets/blobs/beams";
  if (dIndex < 5.5) return "STEVE";
  if (dIndex < 6.5) return "SAR arcs, curtains";
  return "Corona";
}

function getDIndexRGBColor(dIndex: number): string {
  if (dIndex < 3) return "#22c55e";
  if (dIndex < 5) return "#eab308";
  return "#ef4444";
}

interface AuroraForecasterRadialProps {
  /** 0 = dIndex 0, 1 = dIndex 7 - used when scrolling */
  scrollProgress?: number;
  /** Compact card mode: chart only */
  variant?: "full" | "card";
}

export function AuroraForecasterRadial({
  scrollProgress,
  variant = "full",
}: AuroraForecasterRadialProps) {
  const D_INDEX_MAX = 7;
  const useScroll = scrollProgress !== undefined;
  const progress = useScroll ? scrollProgress : 0;
  // Use continuous value for smooth recharts animation
  const dIndexValue = progress * D_INDEX_MAX;
  const dIndexDisplay = Math.round(dIndexValue);
  const dIndexDescription = getDIndexDescription(dIndexValue);
  const fillColor = getDIndexRGBColor(dIndexValue);

  // Keep a fixed scale so only dIndex 7 renders as a full arc.
  const chartData = [
    { dIndex: dIndexValue, transparentData: D_INDEX_MAX - dIndexValue },
  ];

  const isCard = variant === "card";
  const chartWidth = isCard ? undefined : 420;
  const chartHeight = isCard ? undefined : 420;

  const fallback = (
    <div className="flex flex-col items-center justify-center gap-2 p-4 text-center">
      <span className="text-2xl font-bold text-[var(--color-text)]">{dIndexDisplay}</span>
      <span className="text-sm text-[var(--color-text-muted)]">{dIndexDescription}</span>
    </div>
  );

  const chart = (
    <ChartErrorBoundary fallback={fallback}>
    <ChartContainer
      config={{}}
      className={`w-full h-full ${isCard ? "min-h-0 overflow-hidden" : "min-h-[220px] overflow-visible"}`}
      width={chartWidth}
      height={chartHeight}
    >
      <RadialBarChart
        data={chartData}
        startAngle={180}
        endAngle={0}
        cx="50%"
        cy={isCard ? "55%" : "72%"}
        innerRadius={isCard ? "45%" : "58%"}
        outerRadius={isCard ? "75%" : "92%"}
        margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
      >
        <PolarAngleAxis type="number" domain={[0, D_INDEX_MAX]} tick={false} />
        <PolarRadiusAxis
          tick={false}
          tickLine={false}
          axisLine={false}
          domain={[0, D_INDEX_MAX]}
        >
          <Label
            content={({ viewBox }) => {
              if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                const { cx, cy, outerRadius } = viewBox;
                const radius = typeof outerRadius === "number" ? outerRadius : 100;
                const fontSize = radius * 0.25;
                const smallFontSize = radius * 0.12;
                const spacing = radius * 0.08;

                return (
                  <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle">
                    <tspan
                      x={cx}
                      y={(cy ?? 0) - spacing}
                      className="fill-[var(--color-text)] font-bold"
                      style={{ fontSize: `${fontSize}px` }}
                    >
                      {dIndexDisplay}
                    </tspan>
                    <tspan
                      x={cx}
                      y={(cy ?? 0) + fontSize * 0.7}
                      className="fill-[var(--color-text-muted)]"
                      style={{ fontSize: `${smallFontSize}px` }}
                    >
                      {dIndexDescription}
                    </tspan>
                  </text>
                );
              }
            }}
          />
        </PolarRadiusAxis>
        <RadialBar
          dataKey="dIndex"
          stackId="a"
          cornerRadius={5}
          fill={fillColor}
          className="stroke-transparent stroke-2"
          isAnimationActive={false}
        />
        <RadialBar
          dataKey="transparentData"
          fill="transparent"
          stackId="a"
          cornerRadius={5}
          className={`${dIndexValue < 0.5 ? "stroke-[var(--color-border)]" : "stroke-transparent"} stroke-2`}
          isAnimationActive={false}
        />
      </RadialBarChart>
    </ChartContainer>
    </ChartErrorBoundary>
  );

  if (isCard) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-panel min-h-0 overflow-hidden">
        {chart}
      </div>
    );
  }

  return (
    <div className="mb-4">
      <div className="flex flex-col items-center">
        <div className="w-full max-w-[300px] h-[200px]">{chart}</div>
      </div>
    </div>
  );
}
